import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { ArrowLeft, Check, Copy, Eye, EyeOff, Plug, ShieldCheck } from 'lucide-react';
import { toast } from '@/lib/toast';
import PageContainer from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/usePageTitle';
import api from '@/lib/api';
import { copyText } from '@/lib/clipboard';
import { getHubSpotContext } from '@/lib/hubspotContext';
import type { McpConnection } from '@/types/mcp';

function CopyButton({
  value,
  label,
  text = 'Kopiëren',
}: {
  value: string;
  label: string;
  text?: string;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      variant="outline"
      size="sm"
      aria-label={label}
      onClick={async () => {
        try {
          await copyText(value);
          setCopied(true);
          toast.success(`${label.replace(/ kopiëren$/, '')} gekopieerd`);
        } catch {
          toast.error('Kopiëren lukt hier niet. Selecteer de waarde en kopieer deze handmatig.');
        }
      }}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      <span aria-live="polite">{copied ? 'Gekopieerd' : text}</span>
    </Button>
  );
}

function ConnectionField({
  label,
  value,
  displayValue = value,
}: {
  label: string;
  value: string;
  displayValue?: string;
}) {
  return (
    <div className="py-4 first:pt-0 last:pb-0">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{label}</h3>
        <CopyButton value={value} label={`${label} kopiëren`} />
      </div>
      <code className="block select-text break-all rounded bg-muted p-3 text-sm leading-relaxed">
        {displayValue}
      </code>
    </div>
  );
}

function clientConfig(connection: McpConnection, apiKey = connection.apiKey) {
  return JSON.stringify(
    {
      mcpServers: {
        [connection.serverName]: {
          url: connection.endpointUrl,
          headers: { Authorization: `Bearer ${apiKey}` },
        },
      },
    },
    null,
    2
  );
}

export default function McpConnectionPage() {
  const context = useMemo(() => getHubSpotContext(), []);
  const [connection, setConnection] = useState<McpConnection | null>(null);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [connectionError, setConnectionError] = useState('');
  const [keyVisible, setKeyVisible] = useState(false);
  const [retry, setRetry] = useState(0);
  const activeConnection = connection;
  usePageTitle('MCP verbinden');

  const contextQuery = new URLSearchParams();
  if (context.portalId) contextQuery.set('portalId', context.portalId);
  if (context.dealId) contextQuery.set('dealId', context.dealId);
  const query = contextQuery.size ? `?${contextQuery}` : '';
  const quotesHref = `/quotes${query}`;

  useEffect(() => {
    setConnection(null);
    setConnectionError('');
    setKeyVisible(false);
    let active = true;
    setConnectionLoading(true);
    api
      .getMcpConnection()
      .then((details) => {
        if (active) setConnection(details);
      })
      .catch((error: AxiosError<{ message?: string }>) => {
        if (!active) return;
        setConnectionError(
          error.response?.data?.message ||
            'Verbindingsgegevens konden niet worden geladen. Probeer het opnieuw.'
        );
      })
      .finally(() => {
        if (active) setConnectionLoading(false);
      });
    return () => {
      active = false;
    };
  }, [retry]);

  return (
    <PageContainer className="[--muted-foreground:210_10%_45%]">
      <Link
        to={quotesHref}
        className="mb-6 inline-flex items-center gap-2 rounded text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Terug naar offertes
      </Link>
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          Verbind je AI-assistent met Offerte UBO
        </h1>
        <p className="mt-2 max-w-[70ch] text-sm leading-relaxed text-muted-foreground">
          Laat je AI-assistent teksten schrijven, pagina’s toevoegen en de investering aanpassen.
          Stel de verbinding één keer in en werk daarna met al je offertes.
        </p>
      </header>

      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-12">
        <div className="order-2 min-w-0 lg:order-1">
          <section aria-labelledby="mcp-steps">
            <h2 id="mcp-steps" className="text-xl font-semibold">
              Zo maak je verbinding
            </h2>
            <ol className="mt-5 space-y-6">
              <li className="flex gap-3">
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold"
                  aria-hidden="true"
                >
                  1
                </span>
                <div>
                  <h3 className="text-sm font-semibold">Voeg een MCP-server toe</h3>
                  <p className="mt-1 max-w-[65ch] text-sm leading-relaxed text-muted-foreground">
                    Open de MCP-instellingen van je AI-assistent en voeg een externe server toe.
                    Kies <strong className="font-semibold text-foreground">Streamable HTTP</strong>{' '}
                    en plak de vaste MCP-URL.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold"
                  aria-hidden="true"
                >
                  2
                </span>
                <div>
                  <h3 className="text-sm font-semibold">Vul de API-key in</h3>
                  <p className="mt-1 max-w-[65ch] text-sm leading-relaxed text-muted-foreground">
                    Kies authenticatie met een API-key of Bearer-token en plak de sleutel. Werkt je
                    client met headers? Gebruik dan{' '}
                    <code className="text-foreground">Authorization</code> als naam en de
                    gekopieerde Authorization-header als waarde.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold"
                  aria-hidden="true"
                >
                  3
                </span>
                <div>
                  <h3 className="text-sm font-semibold">Laat je assistent aan de slag gaan</h3>
                  <p className="mt-1 max-w-[65ch] text-sm leading-relaxed text-muted-foreground">
                    Vraag je assistent om de juiste offerte te zoeken en te lezen. Bijvoorbeeld:
                  </p>
                  <blockquote className="mt-3 border-l border-border pl-3 text-sm leading-relaxed">
                    “Zoek de offerte voor Noord & Partners en maak het plan van aanpak concreter,
                    met duidelijke stappen en resultaten.”
                  </blockquote>
                </div>
              </li>
            </ol>
          </section>
          <section className="mt-8 border-t pt-6" aria-labelledby="mcp-scope">
            <h2 id="mcp-scope" className="flex items-center gap-2 text-base font-semibold">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" /> Eén verbinding voor al je
              offertes
            </h2>
            <p className="mt-2 max-w-[65ch] text-sm leading-relaxed text-muted-foreground">
              Je assistent zoekt offertes met <code>list_quotes</code> en geeft bij elke bewerking
              het offerte-ID mee. Wisselen van offerte kan binnen dezelfde verbinding.
            </p>
            <p className="mt-3 max-w-[65ch] text-sm leading-relaxed text-muted-foreground">
              Wijzigingen worden direct opgeslagen. Sluit de editor tijdens het werken met je
              assistent en open hem daarna opnieuw om de wijzigingen te zien.
            </p>
          </section>
          <section className="mt-8 border-t pt-6" aria-labelledby="mcp-tools">
            <h2 id="mcp-tools" className="text-base font-semibold">
              Wat je assistent kan doen
            </h2>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
              <li>De offerte lezen en teksten of instellingen aanpassen.</li>
              <li>Pagina’s toevoegen, bewerken en in een andere volgorde zetten.</li>
              <li>Investeringstabellen aanpassen en pagina’s verwijderen na bevestiging.</li>
            </ul>
          </section>
        </div>

        <section
          className="order-1 min-w-0 rounded border bg-card p-5 sm:p-6 lg:order-2"
          aria-labelledby="mcp-details"
        >
          <h2 id="mcp-details" className="text-xl font-semibold">
            Verbindingsgegevens
          </h2>
          {activeConnection ? (
            <>
              <p className="mt-1 break-words text-sm text-muted-foreground">
                Deze URL en sleutel gebruik je voor al je offertes.
              </p>
              <div className="my-5 flex flex-wrap items-center gap-x-4 gap-y-1 border-y py-3 text-sm">
                <span className="inline-flex items-center gap-2">
                  <Plug className="h-4 w-4" aria-hidden="true" /> Streamable HTTP
                </span>
                <span className="text-muted-foreground">Authenticatie: Bearer-token</span>
              </div>
              <div className="divide-y">
                <ConnectionField label="MCP-URL" value={activeConnection.endpointUrl} />
                <div className="py-4">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">API-key</h3>
                    <CopyButton value={activeConnection.apiKey} label="API-key kopiëren" />
                  </div>
                  <div className="flex items-center gap-2 rounded bg-muted p-3">
                    <code className="min-w-0 flex-1 select-text break-all text-sm leading-relaxed">
                      {keyVisible ? activeConnection.apiKey : '••••••••••••••••••••••••'}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="shrink-0"
                      aria-label={keyVisible ? 'API-key verbergen' : 'API-key tonen'}
                      aria-pressed={keyVisible}
                      onClick={() => setKeyVisible((visible) => !visible)}
                    >
                      {keyVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </Button>
                  </div>
                </div>
                <ConnectionField
                  label="Authorization-header"
                  value={`Bearer ${activeConnection.apiKey}`}
                  displayValue={
                    keyVisible
                      ? `Bearer ${activeConnection.apiKey}`
                      : 'Bearer ••••••••••••••••••••••••'
                  }
                />
              </div>
              <div className="mt-6 border-t pt-5">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold">JSON-configuratie</h3>
                  <CopyButton
                    value={clientConfig(activeConnection)}
                    label="JSON-configuratie kopiëren"
                    text="Kopieer configuratie"
                  />
                </div>
                <p className="mb-3 text-sm leading-relaxed text-muted-foreground">
                  Voor clients met een <code>mcpServers</code>-configuratie. De kopieerknop neemt de
                  volledige API-key mee.
                </p>
                <pre className="select-text whitespace-pre-wrap break-all rounded bg-muted p-4 text-xs leading-relaxed">
                  <code>
                    {clientConfig(
                      activeConnection,
                      keyVisible ? activeConnection.apiKey : '<API_KEY>'
                    )}
                  </code>
                </pre>
              </div>
            </>
          ) : connectionLoading ? (
            <div className="mt-6 space-y-5" role="status" aria-label="Verbindingsgegevens laden">
              <span className="sr-only">Verbindingsgegevens laden…</span>
              {[0, 1, 2].map((item) => (
                <div key={item} className="space-y-3">
                  <div className="h-4 w-24 animate-pulse rounded bg-muted motion-reduce:animate-none" />
                  <div className="h-12 animate-pulse rounded bg-muted motion-reduce:animate-none" />
                </div>
              ))}
            </div>
          ) : connectionError ? (
            <div className="mt-5" role="alert">
              <p className="text-sm leading-relaxed text-destructive">{connectionError}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setRetry((value) => value + 1)}
              >
                Opnieuw proberen
              </Button>
            </div>
          ) : (
            <div className="mt-5 border-t py-8">
              <p className="text-sm font-semibold">Verbindingsgegevens laden…</p>
              <p className="mt-2 max-w-[55ch] text-sm leading-relaxed text-muted-foreground">
                Hier verschijnen de vaste MCP-URL, de API-key en de configuratie.
              </p>
            </div>
          )}
        </section>
      </div>
    </PageContainer>
  );
}
